// Classes & Assignments — the unified student "what's due" page.
//
// Layout:
//   1. Live Classes section — Coming Soon teaser (real-time WebRTC
//      lesson rooms are on the P2 roadmap).
//   2. Subject-grouped Assignment list — each subject is its own card
//      column with a count badge, listing pending → overdue → done.
//
// Mounted at /student/assignments via StudentApp routes.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './studentApi';
import { toast } from 'sonner';
import {
  Video, ClipboardList, AlertCircle, Check, CalendarDays, Clock,
  GraduationCap, ArrowRight, Sparkles, Layers, BookOpen,
} from 'lucide-react';

export default function StudentAssignmentsPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');     // 'all' | 'pending' | 'overdue' | 'done'
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get('/student/assignments');
      setData(d);
    } catch (e) {
      toast.error(e.message || 'Could not load assignments');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleDone = async (assignment) => {
    const currentlyDone = assignment.status === 'done';
    setActing(assignment.id);
    try {
      await api.post(`/student/assignments/${assignment.id}/${currentlyDone ? 'mark-pending' : 'mark-done'}`);
      toast.success(currentlyDone ? 'Marked as pending' : 'Nice work — marked done!');
      await load();
      // Tell the header pill to re-count right away so it reflects only
      // what's still outstanding (it otherwise polls on a 2-min interval).
      try { window.dispatchEvent(new CustomEvent('desky:assignments-changed')); } catch { /* noop */ }
    } catch (e) {
      toast.error(e.message || 'Could not update');
    } finally { setActing(null); }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const groups = data.groups || [];
    if (filter === 'all') return groups;
    return groups
      .map(g => ({
        ...g,
        assignments: g.assignments.filter(a => {
          if (filter === 'pending') return a.status !== 'done' && !a.overdue;
          if (filter === 'overdue') return a.overdue;
          if (filter === 'done')    return a.status === 'done';
          return true;
        }),
      }))
      .filter(g => g.assignments.length > 0);
  }, [data, filter]);

  if (!data) return <div className="text-sm opacity-60" data-testid="assignments-loading">Loading assignments…</div>;

  return (
    <div className="space-y-8 max-w-6xl pb-20" data-testid="student-assignments-page">
      <header>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Assignments
        </h1>
        <p className="text-sm opacity-70 mt-1">
          Everything your teachers asked you to do, organised by subject. Submit homework on time to climb the leaderboard.
        </p>
      </header>

      {/* ── 1) Live Classes — Coming Soon ──────────────────────────────── */}
      <section data-testid="live-classes-section">
        <SectionHeader icon={<Video className="w-3.5 h-3.5" />} title="Live Classes" hint="Drop in to your teacher's live room" />
        <div className="rounded-3xl p-6 border-2 relative overflow-hidden"
          style={{
            borderColor: 'var(--st-border)',
            background: 'linear-gradient(135deg, color-mix(in srgb, var(--st-primary) 14%, transparent), color-mix(in srgb, var(--st-accent) 14%, transparent))',
          }}>
          <div className="relative z-10 flex flex-wrap items-center gap-5">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white flex-shrink-0"
              style={{ background: 'var(--st-primary)' }}>
              <Video className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider text-white"
                style={{ background: 'var(--st-accent)' }}>
                <Sparkles className="w-3 h-3" /> Coming Soon
              </div>
              <h3 className="text-xl font-extrabold mt-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Join live classes from inside MyDeskey
              </h3>
              <p className="text-sm opacity-80 mt-1.5 max-w-2xl">
                Real-time HD video rooms, in-class quizzes, hand-raise, breakout discussions and an AI co-tutor that watches with you.
                We're polishing it — invite-only beta opens soon.
              </p>
            </div>
            <button disabled
              data-testid="live-classes-notify-btn"
              className="rounded-full px-5 py-2.5 text-xs font-bold border-2 cursor-not-allowed opacity-70"
              style={{ borderColor: 'var(--st-border)', background: 'white' }}>
              Notify me when ready
            </button>
          </div>
        </div>
      </section>

      {/* ── 2) Assignments ────────────────────────────────────────────── */}
      <section data-testid="assignments-section">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <SectionHeader icon={<ClipboardList className="w-3.5 h-3.5" />}
            title="Assignments" hint={`${data.total} total · ${data.pending} pending · ${data.overdue} overdue`} />
          <FilterTabs value={filter} onChange={setFilter}
            counts={{ all: data.total, pending: data.pending, overdue: data.overdue, done: data.total - data.pending }} />
        </div>

        {filtered.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <div className="space-y-6">
            {filtered.map(group => (
              <SubjectGroup key={group.subject} group={group}
                onOpenTopic={(topicId) => nav(`/student/topic/${topicId}`)}
                onToggleDone={toggleDone}
                acting={acting} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Sub-components
// ════════════════════════════════════════════════════════════════════════

function SubjectGroup({ group, onOpenTopic, onToggleDone, acting }) {
  return (
    <div className="rounded-3xl border-2 overflow-hidden"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
      data-testid={`subject-group-${group.subject}`}>
      {/* Subject header */}
      <div className="px-5 py-3 flex items-center gap-3 border-b"
        style={{ borderColor: 'var(--st-border)', background: 'color-mix(in srgb, var(--st-primary) 6%, transparent)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white"
          style={{ background: 'var(--st-primary)' }}>
          <GraduationCap className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">Subject</div>
          <div className="text-base font-extrabold">{group.subject}</div>
        </div>
        <span className="text-[10px] font-mono px-2 py-1 rounded-full"
          style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}>
          {group.count} item{group.count === 1 ? '' : 's'}
        </span>
      </div>

      {/* Assignment rows */}
      <div className="divide-y" style={{ borderColor: 'var(--st-border)' }}>
        {group.assignments.map(a => (
          <AssignmentRow key={a.id} assignment={a}
            onOpenTopic={() => a.topic_id && onOpenTopic(a.topic_id)}
            onToggleDone={() => onToggleDone(a)}
            acting={acting === a.id} />
        ))}
      </div>
    </div>
  );
}

function AssignmentRow({ assignment: a, onOpenTopic, onToggleDone, acting }) {
  const done = a.status === 'done';
  const overdue = a.overdue;
  const due = a.due_at ? new Date(a.due_at) : null;
  const dueLabel = due ? formatDue(due) : '—';
  return (
    <div className="px-5 py-4 flex items-start gap-4 hover:bg-black/[0.02] transition-colors"
      data-testid={`assignment-row-${a.id}`}
      style={done ? { opacity: 0.6 } : {}}>
      {/* Done checkbox */}
      <button onClick={onToggleDone} disabled={acting}
        data-testid={`assignment-toggle-${a.id}`}
        aria-label={done ? 'Mark as pending' : 'Mark as done'}
        className="w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all hover:scale-105"
        style={{
          borderColor: done ? 'var(--st-primary)' : (overdue ? '#dc2626' : 'var(--st-border)'),
          background: done ? 'var(--st-primary)' : 'transparent',
          color: done ? 'white' : 'transparent',
        }}>
        {done && <Check className="w-3.5 h-3.5" />}
      </button>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className={`text-sm font-bold ${done ? 'line-through' : ''}`}>{a.title}</h4>
          {overdue && !done && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider text-white"
              style={{ background: '#dc2626' }}>
              <AlertCircle className="w-2.5 h-2.5" /> Overdue
            </span>
          )}
          {done && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider text-white"
              style={{ background: 'var(--st-primary)' }}>
              <Check className="w-2.5 h-2.5" /> Done
            </span>
          )}
        </div>
        {a.description && (
          <p className="text-[12px] opacity-70 mt-1 line-clamp-2">{a.description}</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] opacity-70">
          <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {dueLabel}</span>
          {a.due_time && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.due_time}</span>}
          {a.lesson_title && <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3" /> {a.lesson_title}</span>}
          {a.topic_title && <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" /> {a.topic_title}</span>}
          {a.teacher_name && <span className="inline-flex items-center gap-1"><GraduationCap className="w-3 h-3" /> {a.teacher_name}</span>}
          <span className="font-bold" style={{ color: 'var(--st-primary)' }}>+{a.points} pts</span>
        </div>
      </div>

      {/* Open topic */}
      {a.topic_id && (
        <button onClick={onOpenTopic}
          data-testid={`assignment-open-topic-${a.id}`}
          className="text-[11px] font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-full border-2 hover:-translate-y-0.5 transition-all flex-shrink-0"
          style={{ borderColor: 'var(--st-border)', color: 'var(--st-primary)' }}>
          Open lesson <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function FilterTabs({ value, onChange, counts }) {
  const tabs = [
    { id: 'all',     label: 'All',     count: counts.all },
    { id: 'pending', label: 'Pending', count: counts.pending },
    { id: 'overdue', label: 'Overdue', count: counts.overdue },
    { id: 'done',    label: 'Done',    count: counts.done },
  ];
  return (
    <div className="inline-flex p-1 rounded-2xl border-2"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
      data-testid="assignments-filter-tabs">
      {tabs.map(t => {
        const active = value === t.id;
        return (
          <button key={t.id} onClick={() => onChange(t.id)}
            data-testid={`asgn-filter-${t.id}`}
            className="px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all inline-flex items-center gap-1.5"
            style={active ? { background: 'var(--st-primary)', color: '#fff' } : { color: 'inherit' }}>
            {t.label}
            <span className="text-[9px] font-mono px-1.5 py-0 rounded"
              style={active ? { background: 'rgba(255,255,255,0.2)' } : { background: 'var(--st-bg)', color: 'var(--st-mute)' }}>
              {t.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SectionHeader({ icon, title, hint }) {
  return (
    <div className="flex items-baseline gap-2 mb-3">
      <h2 className="text-xs font-bold uppercase tracking-widest opacity-60 flex items-center gap-1.5">{icon} {title}</h2>
      {hint && <span className="text-[11px] opacity-50">· {hint}</span>}
    </div>
  );
}

function EmptyState({ filter }) {
  const labels = {
    all: 'No assignments yet — your teachers will publish them as topics go live.',
    pending: 'No pending assignments. Great work, you\'re all caught up!',
    overdue: 'Nothing overdue. Keep up the pace!',
    done: 'You haven\'t completed any assignments yet — check the Pending tab.',
  };
  return (
    <div className="rounded-2xl p-8 border-2 text-center text-sm opacity-60"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
      {labels[filter] || labels.all}
    </div>
  );
}

function formatDue(d) {
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays === -1) return 'Was due yesterday';
  if (diffDays > 0 && diffDays <= 7) return `Due in ${diffDays} days`;
  if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)}d`;
  return `Due ${d.toLocaleDateString('en', { day: 'numeric', month: 'short' })}`;
}
